import { useState, useCallback } from "react";
import { Pressable } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import { Icon } from "@src/components/Icon";
import { sizes } from "@src/constants/sizes";
import type { CameraApiResponse } from "@src/features/cameras";
import { CameraListItemSidepanel } from "@src/features/cameras/components/CameraListItemSidepanel.web";
import { getCameraKeyString } from "@src/utils/key-parser";

interface SidepanelSiteGroupProps {
  siteName: string;
  siteSubtitle?: string;
  thumbnailUrl?: string | null;
  cameras: CameraApiResponse[];
  selectedCameraKey?: string;
  onCameraSelect: (camera: CameraApiResponse) => void;
  isAlarmSettingsScreen?: boolean;
  defaultExpanded?: boolean;
  collapsed?: boolean;
}

export function SidepanelSiteGroup({
  siteName,
  siteSubtitle,
  thumbnailUrl,
  cameras,
  selectedCameraKey,
  onCameraSelect,
  isAlarmSettingsScreen = false,
  defaultExpanded = false,
  collapsed = false,
}: SidepanelSiteGroupProps) {
  const { theme } = useUnistyles();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [imageError, setImageError] = useState(false);

  const handleToggle = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  return (
    <View mb="lg">
      {/* Site Header */}
      <Pressable
        onPress={handleToggle}
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          padding: theme.spacing.md,
          gap: theme.spacing["2xl"],
          height: 68,
          borderRadius: theme.spacing.md,
        }}
      >
        {/* Left: Thumbnail + Text */}
        <View flexDirection="row" alignItems="center" gap="xl" flex={1}>
          {/* Site Thumbnail */}
          <View
            width={82}
            height={52}
            minHeight={52}
            borderRadius={sizes.radiusSm}
            bg="muted"
            style={{ overflow: "hidden" }}
          >
            {thumbnailUrl && !imageError ?
              <img
                src={thumbnailUrl}
                alt={siteName}
                onError={() => setImageError(true)}
                loading="lazy"
                decoding="async"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            : <View flex={1} alignItems="center" justifyContent="center" bg="muted">
                <Icon name="domain" size={theme.spacing["5xl"]} color={theme.colors.textMuted} />
              </View>
            }
          </View>

          {/* Site Name & Subtitle */}
          <View flex={1} gap="xxs">
            <Text textStyle="sm/bold" color="text" numberOfLines={1}>
              {siteName}
            </Text>
            {siteSubtitle && (
              <Text textStyle="xs/regular" color="textMuted" numberOfLines={1}>
                {siteSubtitle}
              </Text>
            )}
          </View>
        </View>

        {/* Expand/Collapse Icon */}
        <Icon name={isExpanded ? "chevronUp" : "chevronDown"} size={theme.spacing["6xl"]} color={theme.colors.icon} />
      </Pressable>

      {/* Camera List (when expanded) */}
      {isExpanded && (
        <View flexDirection="row" pl="3xl" gap="xl">
          {/* Vertical Line */}
          <View width={2} bg="borderCard" />

          {/* Sub items */}
          <View flex={1} gap="lg">
            {cameras.map((camera) => {
              const cameraKey = getCameraKeyString(camera);
              const isSelected = cameraKey === selectedCameraKey;

              return (
                <CameraListItemSidepanel
                  key={cameraKey}
                  camera={camera}
                  siteId={camera.site.id}
                  onSelect={() => onCameraSelect(camera)}
                  href={
                    isAlarmSettingsScreen ?
                      (`/tenants/${camera.site.tenant}/sites/${camera.site.id}/cameras/${encodeURIComponent(cameraKey)}/alarm` as never)
                    : (`/tenants/${camera.site.tenant}/sites/${camera.site.id}/cameras/${encodeURIComponent(cameraKey)}` as never)
                  }
                  isSelected={isSelected}
                  collapsed={collapsed}
                />
              );
            })}
          </View>
        </View>
      )}
    </View>
  );
}
